import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, Button, TextField, Select, MenuItem, IconButton, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getExpenses, createExpense, deleteExpense, updateExpense, getCostsSummary, getFinance } from '../api';

const COLORS = ['var(--ac1, #7c4dff)','#f44336','#ff9800','#4caf50','#2196f3','#e91e63','#00bcd4','#ff5722'];

const MONTHS_UA = ['Січень','Лютий','Березень','Квітень','Травень','Червень',
  'Липень','Серпень','Вересень','Жовтень','Листопад','Грудень'];

// Дуже приглушені фони: межа місяця має читатись з першого погляду, але не
// перебивати самі записи. Колір закріплений за МІСЯЦЕМ, а не за позицією в
// списку, тому при зміні фільтра місяць лишається того самого відтінку.
const MONTH_TINTS = [
  'rgba(var(--ac1rgb, 124, 77, 255),0.09)', 'rgba(0,188,212,0.09)', 'rgba(76,175,80,0.09)',
  'rgba(255,152,0,0.09)',  'rgba(233,30,99,0.09)', 'rgba(33,150,243,0.09)',
];

const monthKey = (d) => (d || '').slice(0, 7);
const monthLabel = (k) => {
  if (!k) return 'Без дати';
  const [y, m] = k.split('-');
  return (MONTHS_UA[Number(m) - 1] || m) + ' ' + y;
};

export default function ExpensesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [finance, setFinance] = useState(null);
  const [editId, setEditId] = useState(null);
  const [monthFilter, setMonthFilter] = useState('all');
  const [form, setForm] = useState({ name: '', amount: '', type: 'expense', color: '#f44336', date: new Date().toISOString().split('T')[0] });

  const load = () => {
    Promise.all([getExpenses(), getCostsSummary(), getFinance()]).then(([e, c, f]) => {
      setItems(e.data); setSummary(c.data); setFinance(f.data); setLoading(false);
    });
  };
  useEffect(() => { load(); }, []);

  const totalExpenses = items.filter(i => i.type === 'expense').reduce((s, i) => s + parseFloat(i.amount), 0);
  const totalIncome = items.filter(i => i.type === 'income').reduce((s, i) => s + parseFloat(i.amount), 0);
  const aiCosts = parseFloat(summary?.total || 0);
  const revenue = parseFloat(finance?.total || 0);
  const netProfit = revenue - aiCosts - totalExpenses + totalIncome;
  // Підсумок саме по блоку «Записи»: скільки в сухому залишку дають ручні
  // записи разом (доходи мінус витрати) — без AI-витрат і платежів юзерів.
  // Місяці, які реально є в даних (нові — зверху), і сам відбір.
  const availableMonths = [...new Set(items.map(i => monthKey(i.date)))].sort().reverse();
  const visibleItems = monthFilter === 'all'
    ? items
    : items.filter(i => monthKey(i.date) === monthFilter);
  const tintOf = (k) => MONTH_TINTS[Math.max(0, availableMonths.indexOf(k)) % MONTH_TINTS.length];

  // Записи вже приходять з бекенду впорядковані за датою, тож достатньо
  // розрізати список там, де змінюється місяць.
  const monthGroups = [];
  visibleItems.forEach(it => {
    const k = monthKey(it.date);
    const last = monthGroups[monthGroups.length - 1];
    if (last && last.key === k) last.rows.push(it);
    else monthGroups.push({ key: k, rows: [it] });
  });
  const groupNet = (rows) => rows.reduce(
    (s, i) => s + (i.type === 'income' ? parseFloat(i.amount) : -parseFloat(i.amount)), 0);

  // Великий підсумок рахуємо по ВИДИМИХ записах: інакше при фільтрі за місяцем
  // цифра суперечила б списку під нею.
  const shownIncome = visibleItems.filter(i => i.type === 'income')
    .reduce((s, i) => s + parseFloat(i.amount), 0);
  const shownExpenses = visibleItems.filter(i => i.type === 'expense')
    .reduce((s, i) => s + parseFloat(i.amount), 0);
  const shownNet = shownIncome - shownExpenses;

  const handleAdd = async () => {
    if (!form.name || !form.amount) return;
    if (editId) {
      await updateExpense(editId, { ...form, amount: parseFloat(form.amount) });
    } else {
      await createExpense({ ...form, amount: parseFloat(form.amount) });
    }
    setOpen(false);
    setEditId(null);
    setForm({ name: '', amount: '', type: 'expense', color: '#f44336', date: new Date().toISOString().split('T')[0] });
    load();
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({ name: item.name, amount: String(item.amount), type: item.type, color: item.color, date: item.date?.split('T')[0] || new Date().toISOString().split('T')[0] });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити?')) return;
    await deleteExpense(id);
    load();
  };

  if (loading) return <Box sx={{ display:'flex', justifyContent:'center', mt:10 }}><CircularProgress sx={{ color:'var(--ac1, #7c4dff)' }}/></Box>;

  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:3 }}>
        <Typography variant="h5" sx={{ color:'#fff', fontWeight:'bold' }}>💹 Фінансовий облік</Typography>
        <Button startIcon={<AddIcon/>} variant="contained" onClick={() => setOpen(true)} sx={{ bgcolor:'var(--ac1, #7c4dff)' }}>Додати</Button>
      </Box>
      <Grid container spacing={2} sx={{ mb:3 }}>
        {[
          { label:'💰 Дохід (платежі)', value: revenue.toFixed(2) + ' EUR', color:'#4caf50' },
          { label:'🤖 Витрати AI', value: '$' + aiCosts.toFixed(4), color:'#f44336' },
          { label:'📋 Доп. витрати', value: totalExpenses.toFixed(2) + ' EUR', color:'#ff9800' },
          { label:'➕ Доп. дохід', value: totalIncome.toFixed(2) + ' EUR', color:'#2196f3' },
          { label:'✅ Чистий прибуток', value: netProfit.toFixed(2) + ' EUR', color: netProfit >= 0 ? '#4caf50' : '#f44336' },
        ].map(item => (
          <Grid item xs={6} md={2} key={item.label}>
            <Card sx={{ bgcolor:'#1a1a2e', border:'1px solid #333' }}>
              <CardContent sx={{ p:1.5 }}>
                <Typography variant="caption" sx={{ color:'#aaa' }}>{item.label}</Typography>
                <Typography variant="h6" sx={{ color:item.color, fontWeight:'bold', fontSize:16 }}>{item.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Paper sx={{ bgcolor:'#1a1a2e', border:'1px solid #333', p:2 }}>
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
          mb:2, gap:2, flexWrap:'wrap' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5, flexWrap:'wrap' }}>
            <Typography variant="subtitle1" sx={{ color:'var(--ac1, #7c4dff)' }}>📋 Записи</Typography>
            <Select size="small" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
              sx={{ color:'#fff', fontSize:13, minWidth:180,
                '& .MuiOutlinedInput-notchedOutline':{ borderColor:'#333' } }}>
              <MenuItem value="all">Усі місяці ({items.length})</MenuItem>
              {availableMonths.map(k => (
                <MenuItem key={k} value={k}>
                  {monthLabel(k)} ({items.filter(i => monthKey(i.date) === k).length})
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ textAlign:'right', minWidth:200 }}>
            <Typography sx={{ color:'#888', fontSize:11, letterSpacing:.8 }}>
              {monthFilter === 'all' ? 'ПІДСУМОК ЗАПИСІВ' : monthLabel(monthFilter).toUpperCase()}
            </Typography>
            <Typography sx={{ color: shownNet >= 0 ? '#4caf50' : '#f44336', fontWeight:'bold',
              fontSize:36, lineHeight:1.15 }}>
              {(shownNet >= 0 ? '+' : '\u2212') + Math.abs(shownNet).toFixed(2)} EUR
            </Typography>
            <Typography sx={{ color:'#666', fontSize:12 }}>
              +{shownIncome.toFixed(2)} дохід · −{shownExpenses.toFixed(2)} витрати · {visibleItems.length} зап.
            </Typography>
          </Box>
        </Box>
        {visibleItems.length === 0 ? <Typography sx={{ color:'#666' }}>Немає записів</Typography> :
          monthGroups.map(g => (
          <Box key={g.key || 'nodate'} sx={{ mb:2, p:1, borderRadius:1.5,
            bgcolor: tintOf(g.key), border:'1px solid rgba(255,255,255,0.07)' }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', px:0.5, pb:0.75 }}>
              <Typography sx={{ color:'#cfc6ff', fontSize:13, fontWeight:'bold', letterSpacing:.3 }}>
                {monthLabel(g.key)}
                <span style={{ color:'#777', fontWeight:'normal' }}> · {g.rows.length} зап.</span>
              </Typography>
              <Typography sx={{ fontSize:13, fontWeight:'bold',
                color: groupNet(g.rows) >= 0 ? '#4caf50' : '#f44336' }}>
                {(groupNet(g.rows) >= 0 ? '+' : '\u2212') + Math.abs(groupNet(g.rows)).toFixed(2)} EUR
              </Typography>
            </Box>
            {g.rows.map(item => (
            <Box key={item.id} sx={{ display:'flex', alignItems:'center', gap:1, mb:1, p:1, bgcolor:'rgba(255,255,255,0.03)', borderRadius:1 }}>
              <Box sx={{ width:12, height:12, borderRadius:'50%', bgcolor:item.color, flexShrink:0 }}/>
              <Box sx={{ flexGrow:1 }}>
                <Typography variant="body2" sx={{ color:'#fff' }}>{item.name}</Typography>
                <Typography variant="caption" sx={{ color:'#666' }}>{item.date}</Typography>
              </Box>
              <Chip label={item.type === 'expense' ? '-' + item.amount + ' EUR' : '+' + item.amount + ' EUR'}
                size="small" sx={{ bgcolor: item.type === 'expense' ? 'rgba(244,67,54,0.2)' : 'rgba(76,175,80,0.2)',
                  color: item.type === 'expense' ? '#f44336' : '#4caf50' }}/>
              <IconButton size="small" onClick={() => handleEdit(item)} sx={{ color:'var(--ac1, #7c4dff)','&:hover':{color:'#aaa'} }}>
                <EditIcon fontSize="small"/>
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(item.id)} sx={{ color:'#555','&:hover':{color:'#f44'} }}>
                <DeleteIcon fontSize="small"/>
              </IconButton>
            </Box>
            ))}
          </Box>
          ))
        }
      </Paper>
      <Dialog open={open} onClose={() => { setOpen(false); setEditId(null); setForm({ name: '', amount: '', type: 'expense', color: '#f44336', date: new Date().toISOString().split('T')[0] }); }} PaperProps={{ sx:{ bgcolor:'#1a1a2e', border:'1px solid #333', minWidth:300 } }}>
        <DialogTitle sx={{ color:'#fff' }}>{editId ? '✏️ Редагувати' : '➕ Новий запис'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Назва" value={form.name} onChange={e => setForm({...form, name:e.target.value})}
            sx={{ mb:2, mt:1, input:{color:'#fff'}, label:{color:'#aaa'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          <TextField fullWidth type="number" label="Сума (EUR)" value={form.amount} onChange={e => setForm({...form, amount:e.target.value})}
            sx={{ mb:2, input:{color:'#fff'}, label:{color:'#aaa'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          <TextField fullWidth type="date" label="Дата" value={form.date} onChange={e => setForm({...form, date:e.target.value})}
            sx={{ mb:2, input:{color:'#fff'}, label:{color:'#aaa'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          <Select fullWidth value={form.type} onChange={e => setForm({...form, type:e.target.value})}
            sx={{ mb:2, color:'#fff', '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}>
            <MenuItem value="expense">Витрата</MenuItem>
            <MenuItem value="income">Дохід</MenuItem>
          </Select>
          <Box sx={{ display:'flex', gap:1, flexWrap:'wrap' }}>
            {COLORS.map(c => (
              <Box key={c} onClick={() => setForm({...form, color:c})}
                sx={{ width:28, height:28, borderRadius:'50%', bgcolor:c, cursor:'pointer',
                  border: form.color === c ? '3px solid #fff' : '2px solid transparent' }}/>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} sx={{ color:'#aaa' }}>Скасувати</Button>
          <Button onClick={handleAdd} variant="contained" sx={{ bgcolor:'var(--ac1, #7c4dff)' }}>Зберегти</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}